import React, {useEffect} from "react";
import {Button, DialogActions, DialogContent, Grid} from "@mui/material";
import {FormProvider, useForm, useFormContext} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import CustomDialog from "../custom-dialog";
import CustomDialogTitle from "../custom-dialog-title";
import PasswordInput from "../password-input";
import {requestAiSettingsChangePassword} from "../../state/ai-assistant/aiAssistantAction";
import {externalServicesSelector} from "../../state/config/configSelectors";

/***** FormPasswordField *****/

const FormPasswordField = ({label, name, rules = {}, required = false, ...props}) => {
  const {
    register,
    formState: {errors}
  } = useFormContext();
  const hasError = !!errors[name];

  return (
    <PasswordInput
      {...register(name, rules)}
      label={label}
      error={hasError}
      helperText={errors[name]?.message}
      required={required}
      fullWidth
      variant="outlined"
      {...props}
    />
  );
};

/***** AiAuthChangePasswordDialog *****/

const AiAuthChangePasswordDialog = ({open, onClose}) => {
  const {t} = useTranslation();

  const baseUrl = useSelector(externalServicesSelector)?.ai;

  const dispatch = useDispatch();
  const methods = useForm({mode: "onChange"});

  const {
    handleSubmit,
    getValues,
    reset,
    trigger,
    watch,
    formState: {isValid}
  } = methods;

  const newPassword = watch("newPassword");
  const newPasswordConfirm = watch("newPasswordConfirm");

  useEffect(() => {
    if (newPassword && newPasswordConfirm) {
      void trigger("newPasswordConfirm");
    }
  }, [newPassword, newPasswordConfirm, trigger]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = data => {
    dispatch(requestAiSettingsChangePassword(data.oldPassword, data.newPassword, baseUrl));
    handleClose();
  };

  return (
    <CustomDialog maxWidth="sm" open={open} onClose={handleClose}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <CustomDialogTitle onClose={handleClose}>
            {t("modules.aiAssistant.aiSettings.changePasswordDialog.title")}
          </CustomDialogTitle>
          <DialogContent>
            <Grid container spacing={3}>
              <Grid size={12}>
                <FormPasswordField
                  name="oldPassword"
                  label={t("modules.aiAssistant.aiSettings.changePasswordDialog.fields.oldPassword")}
                  rules={{
                    required: t("modules.aiAssistant.aiSettings.changePasswordDialog.oldPassworRequired")
                  }}
                  sx={{
                    marginTop: "4px"
                  }}
                />
              </Grid>
              <Grid size={12}>
                <FormPasswordField
                  name="newPassword"
                  label={t("modules.aiAssistant.aiSettings.changePasswordDialog.fields.newPassword")}
                  rules={{
                    required: t("modules.aiAssistant.aiSettings.changePasswordDialog.newPassworRequired")
                  }}
                  disableAutocomplete
                />
              </Grid>
              <Grid size={12}>
                <FormPasswordField
                  name="newPasswordConfirm"
                  label={t("modules.aiAssistant.aiSettings.changePasswordDialog.fields.newPasswordConfirm")}
                  rules={{
                    required: t("modules.aiAssistant.aiSettings.changePasswordDialog.newPassworRequired"),
                    validate: v =>
                      v === getValues("newPassword") ||
                      t("modules.aiAssistant.aiSettings.changePasswordDialog.passwordNotMatch")
                  }}
                  disableAutocomplete
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>{t("commons.confirm.cancel")}</Button>
            <Button type="submit" variant="text" disabled={!isValid}>
              {t("commons.confirm.submit")}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </CustomDialog>
  );
};

export default AiAuthChangePasswordDialog;
